import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVARRA_REF = /^31\d{16}[A-Z]{2}$/;

// Navarra energy label for a RES022 dossier: the server searches Navarra's public register by
// cadastral reference (about a minute), downloads the label and saves it in "Etiqueta Certificado".
// Shown only for Navarra references with no label saved yet. onDone() is called when it is saved.
// Self-contained: to remove it, delete this file, EtiquetaNavarra in DossierDetailRes022.jsx and
// the backend routes/etiqueta_navarra_v1.py.
const EtiquetaNavarra = ({ dossierId, refCatastral, onDone }) => {
  const aplica = NAVARRA_REF.test((refCatastral || "").trim().toUpperCase());
  const [st, setSt] = useState(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const cargar = async () => {
    try {
      const r = await axios.get(`${API}/etiqueta-navarra/${dossierId}`);
      setSt(r.data);
      return r.data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (aplica) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId, aplica]);

  // While a search runs (started here or found running on load), check it every 4 seconds.
  const buscando = st?.estado === "buscando";
  useEffect(() => {
    if (!aplica || !buscando) return;
    const timer = setInterval(async () => {
      const d = await cargar();
      if (d && d.estado === "ok" && d.ya_existe) {
        toast.success("Etiqueta de Navarra guardada en Documentos Adicionales");
        onDoneRef.current?.();
      }
    }, 4000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aplica, buscando]);

  const pedir = async () => {
    try {
      await axios.post(`${API}/etiqueta-navarra/${dossierId}`);
      await cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo iniciar la búsqueda");
    }
  };

  if (!aplica || !st || st.ya_existe) return null;

  return (
    <Card data-testid="etiqueta-navarra">
      <CardContent className="py-3 flex items-center justify-between gap-3">
        <div className="text-sm">
          <p className="font-medium text-slate-700">Etiqueta energética de Navarra</p>
          <p className="text-xs text-slate-500">Se busca por referencia catastral en el registro de Navarra y se guarda en «Etiqueta Certificado».</p>
          {buscando && <p className="text-xs text-blue-600 mt-1">Buscando en el registro de Navarra… puede tardar 1 minuto.</p>}
          {st.estado === "error" && <p className="text-xs text-red-600 mt-1">{st.mensaje}</p>}
        </div>
        <Button size="sm" variant="outline" onClick={pedir} disabled={buscando} data-testid="etiqueta-navarra-btn">
          {buscando ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Download size={14} className="mr-2" />}
          Descargar etiqueta
        </Button>
      </CardContent>
    </Card>
  );
};

export default EtiquetaNavarra;
